import apiClient from "./api-client";
import { handleApiError } from "./error-handler";
import {
  AccountManagementTask,
  AccountManagementComment,
  CreateAccountManagementCommentRequest,
  UpdateAccountManagementCommentRequest,
  TransferCsmLeadRequest,
  CsmTalentCommentResponse,
} from "../models/AccountManagement";

// ===========================
// TASKS
// ===========================

export const getAccountManagementTasks = async (
  projectId: number
): Promise<AccountManagementTask[]> => {
  try {
    const response = await apiClient.get<AccountManagementTask[]>(
      `/projects/${projectId}/account-management/tasks`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch account management tasks");
  }
};

export const completeAccountManagementTask = async (
  projectId: number,
  taskId: number
): Promise<void> => {
  try {
    await apiClient.patch(
      `/projects/${projectId}/account-management/tasks/${taskId}/complete`
    );
  } catch (error) {
    throw handleApiError(error, "Failed to complete task");
  }
};

/**
 * Reschedules a Planned task to a new date. The backend rejects past dates and
 * any non-Planned status with a 400 — handle the error to show a meaningful
 * snackbar to the user.
 */
export const rescheduleAccountManagementTask = async (
  projectId: number,
  taskId: number,
  newDate: string // ISO 8601, e.g. "2026-06-15T00:00:00Z"
): Promise<void> => {
  try {
    await apiClient.patch(
      `/projects/${projectId}/account-management/tasks/${taskId}/reschedule`,
      { newDate }
    );
  } catch (error) {
    throw handleApiError(error, "Failed to reschedule task");
  }
};

/**
 * Cancels a task — sets its status to Cancelled. The row is kept for traceability
 * (visible in the table with a cancelled visual). Refused on terminal states
 * (Completed / Published / already Cancelled).
 */
export const cancelAccountManagementTask = async (
  projectId: number,
  taskId: number
): Promise<void> => {
  try {
    await apiClient.patch(
      `/projects/${projectId}/account-management/tasks/${taskId}/cancel`
    );
  } catch (error) {
    throw handleApiError(error, "Failed to cancel task");
  }
};

/**
 * Triggers immediate execution of this project's due tasks (Status=Planned,
 * ScheduledDate <= today) instead of waiting for the daily run. The backend
 * enforces that only the project's CSM Lead or an Admin can trigger it.
 */
export const triggerAccountManagementExecution = async (
  projectId: number
): Promise<void> => {
  try {
    await apiClient.post(
      `/projects/${projectId}/account-management/run-due-tasks`
    );
  } catch (error) {
    throw handleApiError(error, "Failed to trigger account management execution");
  }
};

// ===========================
// COMMENTS
// ===========================

export const getAccountManagementComments = async (
  projectId: number
): Promise<AccountManagementComment[]> => {
  try {
    const response = await apiClient.get<AccountManagementComment[]>(
      `/projects/${projectId}/account-management/comments`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch comments");
  }
};

export const createAccountManagementComment = async (
  projectId: number,
  data: CreateAccountManagementCommentRequest
): Promise<AccountManagementComment> => {
  try {
    const response = await apiClient.post<AccountManagementComment>(
      `/projects/${projectId}/account-management/comments`,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to create comment");
  }
};

export const updateAccountManagementComment = async (
  projectId: number,
  commentId: number,
  data: UpdateAccountManagementCommentRequest
): Promise<AccountManagementComment> => {
  try {
    const response = await apiClient.put<AccountManagementComment>(
      `/projects/${projectId}/account-management/comments/${commentId}`,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to update comment");
  }
};

// Note: comment deletion is intentionally NOT exposed.
// Once a comment is created, the linked AM task is marked as Completed.
// Deletion would break this invariant. Comments can be edited, not deleted.

// ===========================
// CSM LEAD
// ===========================

export const transferCsmLead = async (
  projectId: number,
  data: TransferCsmLeadRequest
): Promise<void> => {
  try {
    await apiClient.patch(`/projects/${projectId}/csm-lead`, data);
  } catch (error) {
    throw handleApiError(error, "Failed to transfer CSM Lead");
  }
};

// "Commentaire CSM sur le talent" notes for a project (stored as ApplicationNote
// but exposed here so the AM tab can show the CSM Lead his previous talent comments).
// Type definition lives in models/AccountManagement.ts.

export const getCsmTalentComments = async (
  projectId: number
): Promise<CsmTalentCommentResponse[]> => {
  try {
    const response = await apiClient.get<CsmTalentCommentResponse[]>(
      `/projects/${projectId}/account-management/csm-talent-comments`
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error, "Failed to fetch CSM talent comments");
  }
};
